import type { Item } from './BaseSelectModal/SelectModal'

// BaseSelectModal's item type is constrained to `{ id: string | number }`, so we
// can't use a null id for the "clear" option. Use a numeric sentinel instead and
// translate it back to `origin_campaign_id: null` in the modal's apiAction.
export const CLEAR_ORIGIN_CAMPAIGN_VALUE = -1

// BaseSelectModal derives `item_id` from the selected item's `original.id`, so
// each option must carry an `original` (the campaign options hook does not).
export type CampaignSelectItem = { id: number }

type CampaignOptionInput = { label: string; value: number | string }

/**
 * Adapt the campaign options hook output into BaseSelectModal items.
 *
 * The hook returns `{ label, value }` with no `original`; BaseSelectModal reads
 * the selected item's `original.id` for the API call, so without this each pick
 * would send `item_id: null`. We attach `original.id = campaign id`, prepend a
 * "No campaign" option that maps to `id: null` (clear), and drop any non-numeric
 * sentinel (e.g. the hook's own 'REMOVE') so ids stay clean.
 *
 * Kept JSX/CSS-free in its own module so it is unit-testable under the repo's
 * `node` vitest environment without pulling the modal's style imports.
 */
export function buildCampaignSelectItems(
  campaignOptions: CampaignOptionInput[]
): Item<CampaignSelectItem>[] {
  return [
    {
      label: 'No campaign',
      value: CLEAR_ORIGIN_CAMPAIGN_VALUE,
      original: { id: CLEAR_ORIGIN_CAMPAIGN_VALUE },
    },
    ...campaignOptions
      .filter((option) => typeof option.value === 'number')
      .map((option) => ({
        label: option.label,
        value: option.value as number,
        original: { id: option.value as number },
      })),
  ]
}
